import { FormDrawer, FormDrawerContent, Id, isSuccess } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { UserTenantMultiSelect } from "../../../../shared/ui/UserTenantSelect";
import { birthdayWidgetService } from "../../dashboard/widgets/birthdays/service";
import { contactService } from "../../shared/api/contactService";
import { LanguageSingleSelectField } from "../../shared/select/LanguageSingleSelectField";
import { IContactBaseInfoFormState, IContactUpdateResource } from "../../shared/types";
import { useGlobalTenants } from "../../tenants/global/GlobalTenantProvider";

export const ContactUpdateBaseInfoDrawer: FC<{
	open: boolean;
	onClose: () => void;
	contact: IContactUpdateResource;
}> = ({ open, onClose, contact }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { selected } = useGlobalTenants();

	const handleUpdate = useCallback(
		async (contactId: Id, values: IContactBaseInfoFormState) => {
			const response = await contactService.updateBaseInfo(contactId, {
				locale: values.locale,
				tenantIds: values.tenants.map((x) => x.id),
			});
			const isOk = isSuccess(response);

			if (isOk) {
				await queryClient.invalidateQueries({ queryKey: contactService.showKey(contactId) });
				await queryClient.invalidateQueries({ queryKey: contactService.listKey });
				await queryClient.invalidateQueries({ queryKey: birthdayWidgetService.key });

				if (selected.tenant && values.tenants.map((tenant) => tenant.id).includes(selected.tenant.id)) {
					onClose();
				} else {
					navigate("/contacts");
				}
			}
			return isOk;
		},
		[queryClient, selected.tenant, onClose, navigate],
	);

	const validate = useCallback(
		(values: IContactBaseInfoFormState) => {
			const errors: { [K in keyof IContactBaseInfoFormState]?: any } = {};
			if (!values.locale) {
				errors.locale = t("Required field");
			}
			if (!values.tenants.length) {
				errors.tenants = t("Required field");
			}
			return errors;
		},
		[t],
	);

	const createInitialValues = useMemo(
		(): IContactBaseInfoFormState => ({
			locale: contact.locale,
			tenants: contact.tenants,
		}),
		[contact.locale, contact.tenants],
	);

	return (
		<FormDrawer
			title={t("Edit contact")}
			open={open}
			onClose={onClose}
			initialValues={createInitialValues}
			validate={validate}
			onSubmit={(values) => handleUpdate(contact.id, values)}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<LanguageSingleSelectField name="locale" label={t("Locale")} required />
					<UserTenantMultiSelect
						name="tenants"
						label={t("Tenants")}
						required
						getLabel={(x) =>
							contact.archivedByTenants.some((y) => y.id === x.id) ? `(${t("Archived")}) ${x.name}` : x.name
						}
					/>
				</FormDrawerContent>
			)}
		/>
	);
};
